import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { Menu } from '@material-ui/core';
import { useIntl } from 'react-intl';
import ChartDateRangeQuarterOption from './ChartDateRangeQuarterOption';

export const quarterFormat = start => start.toFormat('Qq yyyy');

export const quarterRangeFormat = (start, end) =>
  `${start.toLocaleString({ month: 'short' })} — ${end.toLocaleString({
    month: 'short',
    year: 'numeric'
  })}`;

const menuListProps = {
  dense: true,
  role: 'menu'
};

const DEFAULT_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'left' };
const DEFAULT_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'left' };
const PREVIOUS_COUNT = 5;
const NEXT_COUNT = 5;

export const createQuarterOptions = ({
  date,
  currentQuarterLabel,
  currentQuarterStartDate,
  isRmp2025Q3Enhancements = false
}) => {
  const periodCount = PREVIOUS_COUNT + 1 + NEXT_COUNT;
  const periods = new Array(periodCount).fill({}).map((_, index) => {
    const quarterDate = new DateTime(date).plus({
      quarter: index - PREVIOUS_COUNT
    });

    const quarterDateDT = new DateTime(quarterDate);
    const periodStart = quarterDateDT.startOf('quarter');
    const periodEnd = quarterDateDT.endOf('quarter');

    const isCurrentQuarter =
      isRmp2025Q3Enhancements &&
      periodStart.toISODate() === currentQuarterStartDate.toISODate();
    const displayLabel = isCurrentQuarter
      ? currentQuarterLabel
      : quarterFormat(periodStart);

    return {
      key: quarterDate.toFormat('yyyy-MM-DD'),
      label: displayLabel,
      dateLabel: quarterRangeFormat(periodStart, periodEnd),
      isSelected: index - PREVIOUS_COUNT === 0,
      value: {
        startDate: periodStart,
        endDate: periodEnd
      }
    };
  });

  return periods;
};

export const ChartDateRangeQuarterPicker = ({
  dateRange,
  menuId,
  anchorEl,
  onChange,
  onClose,
  isRmp2025Q3Enhancements = false
}) => {
  const isOpen = Boolean(anchorEl);
  const { formatMessage } = useIntl();

  const handleQuarterMenuItemClick = useCallback(
    value => {
      if (onChange) onChange(value);
      if (onClose) onClose();
    },
    [onChange, onClose]
  );

  const currentQuarterStartDate = DateTime.local().startOf('quarter');

  const currentQuarterLabel = isRmp2025Q3Enhancements
    ? formatMessage(
        { id: 'chart.currentQuarterLabel' },
        {
          value: quarterFormat(currentQuarterStartDate)
        }
      )
    : '';

  const quarterOptions = createQuarterOptions({
    date: dateRange.startDate,
    currentQuarterLabel,
    currentQuarterStartDate,
    isRmp2025Q3Enhancements
  });

  return (
    <Menu
      id={menuId}
      anchorEl={anchorEl}
      anchorOrigin={DEFAULT_ANCHOR_ORIGIN}
      transformOrigin={DEFAULT_TRANSFORM_ORIGIN}
      open={isOpen}
      onClose={onClose}
      MenuListProps={menuListProps}
      PaperProps={{
        role: 'main',
        'aria-label': formatMessage({ id: 'chart.selectQuarter' })
      }}
    >
      {quarterOptions.map(({ key, ...option }) => (
        <ChartDateRangeQuarterOption
          key={key}
          {...option}
          onClick={handleQuarterMenuItemClick}
        />
      ))}
    </Menu>
  );
};

ChartDateRangeQuarterPicker.propTypes = {
  dateRange: PropTypes.object.isRequired,
  menuId: PropTypes.string.isRequired,
  anchorEl: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isRmp2025Q3Enhancements: PropTypes.bool
};

export default ChartDateRangeQuarterPicker;
